// Property indicator badges for region overlays.
//
// Each event can carry reviewable flags (notes / notesCheck / reply /
// replyCheck / approved / forced / render required). Rather than fight
// Konva.Path rendering at tiny sizes we render each indicator as a small
// colored rounded square with a single-character glyph -- crisp at any zoom,
// no SVG parsing cost.

import { eventNeedsRender } from "@app/lib/audio/audioRenderState.js";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";
import { canEditMetadata } from "@app/store/permissionStore.svelte.js";

/**
 * @typedef {Object} BadgeDef
 * @property {string} key    - icon identifier (matches event property name)
 * @property {string} label  - 1-character glyph
 * @property {string} fill   - background color (with alpha)
 * @property {string} text   - foreground color
 */

/** @type {Record<string, BadgeDef>} */
export const BADGES = {
    notes:          { key: "notes",          label: "N", fill: "#f0ad4e", text: "#1a1a1a" },
    notesCheck:     { key: "notesCheck",     label: "N", fill: "#2ecc71", text: "#1a1a1a" },
    reply:          { key: "reply",          label: "R", fill: "#3498db", text: "#ffffff" },
    replyCheck:     { key: "replyCheck",     label: "R", fill: "#2ecc71", text: "#1a1a1a" },
    approved:       { key: "approved",       label: "\u2713", fill: "#2ecc71", text: "#1a1a1a" },
    failed:         { key: "failed",         label: "X", fill: "#e74c3c", text: "#ffffff" },
    forced:         { key: "forced",         label: "FS", fill: "#9b59b6", text: "#ffffff" },
    renderRequired: { key: "renderRequired", label: "!", fill: "#f0ad4e", text: "#1a1a1a" }
};

/** Order badges left-to-right when both members of a "pair" might be active. */
export const BADGE_ORDER = ["notes", "notesCheck", "reply", "replyCheck", "approved", "forced", "renderRequired"];

/**
 * Compute which badges should be visible for an event. notesCheck supersedes
 * notes (and replyCheck supersedes reply) so the same slot is not shown twice.
 *
 * @param {object} event
 * @param {string} [groupType]
 * @returns {string[]}  - subset of `BADGE_ORDER`
 */
export function badgesForEvent(event, groupType) {
    if (!event) return [];
    const out = [];
    const hasNotes = typeof event.notes === "string" && event.notes.length > 0;
    const hasReply = typeof event.reply === "string" && event.reply.length > 0;
    const showNotes = canEditMetadata("notes");
    const showApprovals = canEditMetadata("approvals");
    if (hasNotes && showNotes) out.push(event.notesCheck ? "notesCheck" : "notes");
    if (hasReply && showNotes) out.push(event.replyCheck ? "replyCheck" : "reply");
    if (showApprovals && event.approved === true) out.push("approved");
    if (showApprovals && event.approved === false) out.push("failed");
    if (event.forced && canEditMetadata("forced")) out.push("forced");
    if (isAudioGroupType(groupType) && eventNeedsRender(event)) out.push("renderRequired");
    return out;
}

export const BADGE_SIZE = 12;
export const BADGE_GAP = 2;
